import { useState } from 'react';
import { tx } from '../i18n';
import { pedir } from '../lib/api';
import { permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando, hace } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import type { Rol, UsuarioDelPanel } from '../tipos';
import { Modal } from '../ui/Modal';
import { Cabecera, Consulta, Pastilla, primero, Secreto, Tabla, useAviso, useRefrescar, Vacio, Ventana } from '../ui/piezas';

const ROLES: Rol[] = ['admin', 'soporte', 'ventas'];

/** Alta y edición comparten formulario; el correo solo se elige al crear (es con qué entra). */
function FormUsuario({
  u,
  alCerrar,
  alListo,
}: {
  u: UsuarioDelPanel | null;
  alCerrar: () => void;
  alListo: (r: { nombre: string; clave?: string; correo: string }) => void;
}) {
  const t = tx.usuarios;
  const [nombre, setNombre] = useState(u?.nombre ?? '');
  const [correo, setCorreo] = useState(u?.correo ?? '');
  const [rol, setRol] = useState<Rol>(u?.rol ?? 'soporte');
  return (
    <Ventana
      titulo={u ? t.editarA(u.nombre) : t.nuevo}
      boton={u ? tx.comun.guardar : t.crear}
      alCerrar={alCerrar}
      alEnviar={async () => {
        const falta = primero(!nombre.trim() && t.faltaNombre, !u && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo.trim()) && t.faltaCorreo);
        if (falta) return falta;
        if (u) {
          await pedir(`/usuarios/${u.id}`, { metodo: 'PATCH', cuerpo: { nombre: nombre.trim(), rol } });
          alListo({ nombre: nombre.trim(), correo: u.correo });
        } else {
          const r = await pedir<{ clave: string; usuario: UsuarioDelPanel }>('/usuarios', {
            metodo: 'POST',
            cuerpo: { nombre: nombre.trim(), correo: correo.trim(), rol },
          });
          alListo({ nombre: r.usuario.nombre, correo: r.usuario.correo, clave: r.clave });
        }
      }}
    >
      <label className="campo" htmlFor="us-nombre">
        {tx.comun.nombre}
        <input id="us-nombre" className="entrada" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder={t.nombreEjemplo} />
      </label>
      {!u && (
        <label className="campo" htmlFor="us-correo">
          {t.correo}
          <input
            id="us-correo"
            className="entrada"
            type="email"
            autoComplete="off"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            placeholder={t.correoEjemplo}
          />
        </label>
      )}
      <label className="campo" htmlFor="us-rol">
        {t.rol}
        <select id="us-rol" className="entrada" value={rol} onChange={(e) => setRol(e.target.value as Rol)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {tx.comun.roles[r]}
            </option>
          ))}
        </select>
        <small className="nota">{t.queRol[rol]}</small>
      </label>
    </Ventana>
  );
}

/** La clave temporal, UNA vez. No se cierra con un clic afuera: si se pierde, hay que sacar otra. */
function ClaveTemporal({ correo, clave, alCerrar }: { correo: string; clave: string; alCerrar: () => void }) {
  const t = tx.usuarios;
  return (
    <Modal
      titulo={t.claveDe(correo)}
      alCerrar={alCerrar}
      cerrarAfuera={false}
      pie={
        <button type="button" className="btn prim" onClick={alCerrar}>
          {t.claveGuardada}
        </button>
      }
    >
      <p className="alerta" role="alert">
        <b>{t.unaSolaVez}</b> {t.claveQue}
      </p>
      <Secreto valor={clave} etiqueta={t.claveTemporal} />
    </Modal>
  );
}

type Accion = { que: 'clave' | 'desactivar'; u: UsuarioDelPanel };

/** La gente que entra a este panel. Solo el admin la maneja. */
export function Usuarios({ incrustado = false }: { incrustado?: boolean }) {
  const { usuario: yo } = useSesion();
  const puede = permisos(yo?.rol);
  const [aviso, avisar] = useAviso();
  const refrescar = useRefrescar();
  const [nuevo, setNuevo] = useState(false);
  const [editando, setEditando] = useState<UsuarioDelPanel | null>(null);
  const [accion, setAccion] = useState<Accion | null>(null);
  const [clave, setClave] = useState<{ correo: string; clave: string } | null>(null);
  const t = tx.usuarios;

  const lista = useConsulta<UsuarioDelPanel[]>('/usuarios', { enabled: puede.manejarUsuarios });
  const filas = lista.data ?? [];
  const recargar = () => void refrescar('/usuarios', '/bitacora');

  const cambiar = async (u: UsuarioDelPanel, cuerpo: object, texto: string, ruta = `/usuarios/${u.id}`, metodo: 'PATCH' | 'POST' = 'PATCH') => {
    try {
      await pedir(ruta, { metodo, cuerpo });
      avisar(texto);
    } catch (e) {
      avisar((e as Error).message, true);
    }
    recargar();
  };

  if (!puede.manejarUsuarios)
    return (
      <div className="pagina">
        <Cabecera titulo={t.titulo}>{t.que}</Cabecera>
        <Vacio>{t.soloAdmin}</Vacio>
      </div>
    );

  const nuevoBoton = (
    <button type="button" className="btn prim" onClick={() => setNuevo(true)}>
      {t.nuevo}
    </button>
  );
  return (
    // dentro de Ajustes es una pestaña: sin la cabecera de página, con su botón al lado
    <div className={incrustado ? 'pestana-cuerpo' : 'pagina'}>
      {incrustado ? (
        <div className="pestana-cab">
          <p className="nota">{t.que}</p>
          {nuevoBoton}
        </div>
      ) : (
        <Cabecera titulo={t.titulo} accion={nuevoBoton}>
          {t.que}
        </Cabecera>
      )}
      {aviso}

      <section className="panel">
        <Consulta q={lista} vacio={lista.data && filas.length === 0 ? <Vacio>{t.ninguno}</Vacio> : undefined}>
          <div className="scroll-x">
            <Tabla>
              <thead>
                <tr>
                  <th>{tx.comun.nombre}</th>
                  <th>{t.correo}</th>
                  <th>{t.rol}</th>
                  <th>{t.estado}</th>
                  <th>{t.ultimoIngreso}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filas.map((u) => {
                  const soyYo = u.id === yo?.id;
                  return (
                    <tr key={u.id} className={u.activo ? '' : 'apagado'}>
                      <td>
                        <b>{u.nombre}</b>
                        {soyYo && <span className="nota"> · {t.vos}</span>}
                      </td>
                      <td className="d num">{u.correo}</td>
                      <td>{tx.comun.roles[u.rol]}</td>
                      <td>
                        {!u.activo ? (
                          <Pastilla tono="desconocido">{t.inactivo}</Pastilla>
                        ) : u.bloqueado_hasta ? (
                          <Pastilla tono="parcial">{t.bloqueadoHasta(cuando(u.bloqueado_hasta) ?? '—')}</Pastilla>
                        ) : (
                          <Pastilla tono="arriba">{t.activo}</Pastilla>
                        )}
                      </td>
                      <td className="d">{u.ultimo_ingreso ? hace(u.ultimo_ingreso) : t.nunca}</td>
                      <td className="acciones der">
                        <button type="button" className="btn chico" onClick={() => setEditando(u)}>
                          {tx.comun.editar}
                        </button>
                        <button type="button" className="btn chico" onClick={() => setAccion({ que: 'clave', u })}>
                          {t.claveNueva}
                        </button>
                        {u.activo && u.bloqueado_hasta && (
                          <button
                            type="button"
                            className="btn chico"
                            onClick={() => void cambiar(u, {}, t.desbloqueado(u.nombre), `/usuarios/${u.id}/desbloquear`, 'POST')}
                          >
                            {t.desbloquear}
                          </button>
                        )}
                        {!soyYo &&
                          (u.activo ? (
                            <button type="button" className="btn chico peligro" onClick={() => setAccion({ que: 'desactivar', u })}>
                              {t.desactivar}
                            </button>
                          ) : (
                            <button type="button" className="btn chico" onClick={() => void cambiar(u, { activo: true }, t.activado(u.nombre))}>
                              {t.activar}
                            </button>
                          ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabla>
          </div>
        </Consulta>
      </section>

      {nuevo && (
        <FormUsuario
          u={null}
          alCerrar={() => setNuevo(false)}
          alListo={(r) => {
            setNuevo(false);
            recargar();
            if (r.clave) setClave({ correo: r.correo, clave: r.clave });
            avisar(t.creado(r.nombre));
          }}
        />
      )}
      {editando && (
        <FormUsuario
          u={editando}
          alCerrar={() => setEditando(null)}
          alListo={(r) => {
            setEditando(null);
            recargar();
            avisar(t.guardado(r.nombre));
          }}
        />
      )}
      {accion?.que === 'clave' && (
        <Ventana
          titulo={t.claveNuevaPara(accion.u.nombre)}
          boton={t.claveNueva}
          alCerrar={() => setAccion(null)}
          alEnviar={async () => {
            const r = await pedir<{ clave: string }>(`/usuarios/${accion.u.id}/clave`, { metodo: 'POST' });
            setAccion(null);
            recargar();
            setClave({ correo: accion.u.correo, clave: r.clave });
          }}
        >
          <p>{t.claveNuevaQue}</p>
        </Ventana>
      )}
      {accion?.que === 'desactivar' && (
        <Ventana
          titulo={t.desactivarA(accion.u.nombre)}
          boton={t.desactivar}
          peligro
          alCerrar={() => setAccion(null)}
          alEnviar={async () => {
            await pedir(`/usuarios/${accion.u.id}`, { metodo: 'PATCH', cuerpo: { activo: false } });
            setAccion(null);
            recargar();
            avisar(t.desactivado(accion.u.nombre));
          }}
        >
          <p>{t.desactivarQue}</p>
        </Ventana>
      )}
      {clave && <ClaveTemporal correo={clave.correo} clave={clave.clave} alCerrar={() => setClave(null)} />}
    </div>
  );
}
